import React, { useContext, useEffect } from "react";
import { useActions } from "../hooks/useActions";
import { useTypedSelector } from "../hooks/useTypedSelector";
import Loading, { LoadingBall } from "@ingka/loading";
import Styles from "./HomeScreen.module.css";
import "@ingka/svg-icon/dist/style.css";
import "@ingka/loading/dist/style.css";
import { ViewKCIContext } from "../contexts/ViewKCIContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import AgGrid from "../components/AgGrid";
import { useAuth } from "../auth";
import Tabs, { Tab, TabPanel } from "@ingka/tabs";
import { isProvidedServiceNotification } from "../utils/notificationClassification";

const ALLOWED_TABS = ["delivery", "service"] as const;
const DEFAULT_TAB = "delivery";

const HomeScreen: React.FC = () => {
  const { getCountryKCI } = useActions(useDispatch());
  const { getAccessTokenSilently } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewKCIContext = useContext(ViewKCIContext);
  const { data, loading, error } = useTypedSelector(
    (state) => state.homeScreen
  );
  const tabParam = searchParams.get("tab");
  const validatedTab = ALLOWED_TABS.includes(tabParam as any)
    ? tabParam
    : DEFAULT_TAB;
  const [activeTab, setActiveTab] = React.useState<string>(validatedTab!);

  useEffect(() => {
    if (!viewKCIContext?.country) {
      return;
    }

    const fetchDataAndDispatch = async () => {
      const accessToken = await getAccessTokenSilently();
      getCountryKCI(viewKCIContext.country, accessToken);
    };

    fetchDataAndDispatch();
  }, [getAccessTokenSilently, getCountryKCI, viewKCIContext?.country]);

  const getToHome = () => {
    navigate("/country");
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`/home?tab=${tabId}`);
  };

  if (error) {
    getToHome();
  }

  const deliveryNotifications = data.filter(
    (notification) => !isProvidedServiceNotification(notification)
  );
  const providedServiceNotifications = data.filter(
    isProvidedServiceNotification
  );

  return (
    <div data-testid="home-screen">
      {data.length > 0 ? (
        <div style={{ padding: "2rem" }}>
          <Tabs
            defaultActiveTab={activeTab}
            onTabChanged={(tabId) => {
              handleTabChange(tabId);
            }}
            tabPanels={[
              <TabPanel key="delivery" tabPanelId="delivery">
                <AgGrid
                  data={deliveryNotifications}
                  isProvidedServicesView={false}
                />
              </TabPanel>,
              <TabPanel key="service" tabPanelId="service">
                <AgGrid
                  data={providedServiceNotifications}
                  isProvidedServicesView={true}
                />
              </TabPanel>,
            ]}
            tabs={[
              <Tab
                key="delivery"
                tabPanelId="delivery"
                text="Delivery Notifications"
              />,
              <Tab
                key="service"
                tabPanelId="service"
                text="Provided Services Notifications"
              />,
            ]}
          />
        </div>
      ) : (
        !loading &&
        !error && (
          <div className={Styles.noDataContainer}>
            <p>
              Oops! There is no configuration thats available for the selected
              country
            </p>
            <button className={Styles.homeBtn} onClick={getToHome}>
              Home
            </button>
          </div>
        )
      )}
      {loading && (
        <div className={Styles.loadingContainer}>
          <Loading text="Loading..." labelTransitions>
            <LoadingBall />
          </Loading>
        </div>
      )}
      {error && <div className={Styles.loadingContainer}>{error}</div>}
    </div>
  );
};

export default HomeScreen;
